import { useCallback, useState } from "react";

import {
    createCoupon,
    deleteCoupon as deleteCouponRequest,
    updateCoupon,
} from "services/coupons/rest";
import { CouponRequestDto } from "services/coupons/restRequests";
import { UseNotificationSnackbarReturn } from "shared/ui";

export interface UseCouponMutationsResult {
    saving: boolean;
    // true — операция удалась: вызывающий решает, закрывать ли диалог и обновлять ли список.
    saveCoupon: (
        values: CouponRequestDto,
        menuId: number,
        couponId: number | null
    ) => Promise<boolean>;
    removeCoupon: (couponId: number) => Promise<boolean>;
}

/*
* Мутации промокода: флаг saving, тост об успехе и разбор ошибки в одной обёртке run.
* Сами мутации только описывают запрос — состояние фичи от них не разрастается.
* Список после мутации перезапрашивает вызывающий: страница отсортирована и пронумерована
* сервером, поэтому точечная правка массива разошлась бы с total и с содержимым страницы.
* */
export const useCouponMutations = (
    notify: UseNotificationSnackbarReturn
): UseCouponMutationsResult => {
    const [saving, setSaving] = useState<boolean>(false);

    const run = useCallback(
        async (action: () => Promise<unknown>, ok: string, fail: string): Promise<boolean> => {
            setSaving(true);
            try {
                await action();
                notify.showSuccess(ok);
                return true;
            } catch (e) {
                notify.showError(fail, e);
                return false;
            } finally {
                setSaving(false);
            }
        },
        [notify]
    );

    const saveCoupon = useCallback(
        (values: CouponRequestDto, menuId: number, couponId: number | null) =>
            couponId === null
                ? run(
                      () => createCoupon({ menuId, coupon: values }),
                      "Промокод добавлен",
                      "Не удалось создать промокод"
                  )
                : run(
                      () => updateCoupon(couponId, values),
                      "Промокод сохранён",
                      "Не удалось сохранить промокод"
                  ),
        [run]
    );

    const removeCoupon = useCallback(
        (couponId: number) =>
            run(
                () => deleteCouponRequest(couponId),
                "Промокод удалён",
                "Не удалось удалить промокод"
            ),
        [run]
    );

    return { saving, saveCoupon, removeCoupon };
};
